import { useEffect, useId, useMemo, useRef, useState, type CSSProperties, type KeyboardEvent } from 'react';
import { Archive, Search, SearchX } from 'lucide-react';
import { useMe } from '@/hooks/use-me';
import { useObras } from '@/hooks/use-obras';
import { borrarFiltrosPersistidos, guardarFiltrosPersistidos, leerFiltrosPersistidos } from '@/hooks/use-obras-filtros-persistidos';
import { ObraCardTile } from '@/components/obras/obra-card';
import { FiltrosObrasPopover } from '@/components/obras/filtros-obras-popover';
import { MetaObras } from '@/components/obras/meta-obras';
import { EstadoRedesDialog } from '@/components/obras/estado-redes-dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Field, FieldLabel } from '@/components/ui/field';
import { InputGroup, InputGroupAddon, InputGroupInput } from '@/components/ui/input-group';
import { Switch } from '@/components/ui/switch';
import { Empty, EmptyContent, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@/components/ui/empty';
import { ATAJO_BUSCAR, accionEscBuscador, esAtajoBuscar } from '@/components/filtros/atajos';
import { normalizarBusqueda } from '@/components/filtros/tokens';
import { accentVariants, encInitials } from '@/lib/client-color';
import {
  aplicarEnter,
  chipsActivos,
  contarFacetas,
  contarFiltros,
  detectarEnter,
  filtrar,
  limpiarFiltros,
  quitarChip,
  sanearFiltrosObras,
  varianteConteo,
} from '@/components/obras/obras-filtros';
import { ENC_NOMBRES, TODO_EL_EQUIPO_OBRAS, type ChipObraKey, type FiltrosObras, type ResultadoEnterObras } from '@/components/obras/obras-filtros-tipos';
import type { ObraCard } from '@/types/api';

const SEARCH_DEBOUNCE_MS = 250;

const PLACEHOLDER_BUSCADOR = 'Buscar obra, cliente, CC o coordinación… (Enter agrega como filtro)';
const ARIA_BUSCADOR = 'Buscar obras; Enter convierte el texto en un filtro';

// ---------------------------------------------------------------------------
// Sub-componentes de estado (skeleton / error / vacío) y de contenido.
// ---------------------------------------------------------------------------

function SkeletonGrid() {
  return (
    <>
      <span role="status" className="sr-only">
        Cargando obras…
      </span>
      <div aria-hidden="true" className="grid grid-cols-[repeat(auto-fill,minmax(310px,1fr))] gap-3.5">
        {Array.from({ length: 6 }).map((_, i) => (
          <div
            key={i}
            className="flex min-h-[132px] flex-col gap-2.5 rounded-[11px] border border-border-subtle bg-surface-card px-[15px] py-[14px]"
          >
            <Skeleton className="h-[22px] w-[30%] rounded-[5px] bg-surface-2" />
            <Skeleton className="h-3 w-[76%] rounded-[5px] bg-surface-2" />
            <Skeleton className="h-2.5 w-[48%] rounded-[5px] bg-surface-2" />
            <Skeleton className="mt-auto h-2.5 w-[62%] rounded-[5px] bg-surface-2" />
          </div>
        ))}
      </div>
    </>
  );
}

function ErrorState({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="flex flex-col items-center gap-1.5 px-5 py-16 text-center">
      <h3 className="m-0 text-[15px] font-semibold text-foreground">No se pudieron cargar las obras</h3>
      <p className="m-0 mb-3 text-[13px] text-muted-foreground">
        Puede ser un problema de red o del servidor. Probá de nuevo.
      </p>
      <Button type="button" size="sm" onClick={onRetry}>
        Reintentar
      </Button>
    </div>
  );
}

interface EmptyObrasProps {
  hayBusqueda: boolean;
  hayControlRdtActivo: boolean;
  hayOtrosFiltros: boolean;
  archivadas: boolean;
  /** Obras Archivado que coinciden con la búsqueda/filtros vigentes, sin el
   *  switch (síntesis §2 «Vacío»: «N archivadas o No Aplica coinciden · Ver
   *  con Archivadas» — 0 con el switch ya prendido). Junto con
   *  `archNoAplicaCoincidentesN` arma `archCoincTxt` (artboard
   *  `Home.dc.html:527`): separa el caso «solo Archivado» / «solo No
   *  Aplica» / mezcla, en vez de nombrar siempre los dos estados. */
  archArchivadoCoincidentesN: number;
  /** Obras No Aplica que coinciden, sin el switch (ver arriba). */
  archNoAplicaCoincidentesN: number;
  onLimpiarBusqueda: () => void;
  onVerTodoElEquipo: () => void;
  onVerConArchivadas: () => void;
  onLimpiarFiltros: () => void;
}

// Vacío de la home (R4a; síntesis §2 «Vacío»): Empty de ReUI (c-empty-4,
// https://reui.io/preview/base/components/c-empty-4?ref=mcp — sin
// @reui/icon-stack, que no está instalado: EmptyMedia variant="icon" con un
// lucide alcanza, mismo patrón que EmptySinPendientes del Avivador,
// components/avivador/empties-avivador.tsx). El vacío NUNCA queda sin salida:
// «Limpiar filtros» aparece si hay algo del popover elegido, o si ninguna de
// las otras 3 salidas aplica.
function EmptyObras({
  hayBusqueda,
  hayControlRdtActivo,
  hayOtrosFiltros,
  archivadas,
  archArchivadoCoincidentesN,
  archNoAplicaCoincidentesN,
  onLimpiarBusqueda,
  onVerTodoElEquipo,
  onVerConArchivadas,
  onLimpiarFiltros,
}: EmptyObrasProps) {
  const archCoincN = archArchivadoCoincidentesN + archNoAplicaCoincidentesN;
  const hayArchCoincidencia = !archivadas && archCoincN > 0;
  const verLimpiarFiltros = hayOtrosFiltros || (!hayBusqueda && !hayControlRdtActivo && !hayArchCoincidencia);

  // 3 ramas, no siempre «archivadas o No Aplica» (artboard `Home.dc.html:527`,
  // `archCoincTxt` — hallazgo #4 reui-y-artboards): solo Archivado / solo No
  // Aplica / mezcla de los dos.
  const archCoincTxt =
    archNoAplicaCoincidentesN === 0
      ? archCoincN === 1
        ? 'archivada coincide'
        : 'archivadas coinciden'
      : archArchivadoCoincidentesN === 0
        ? archCoincN === 1
          ? 'No Aplica coincide'
          : 'No Aplica coinciden'
        : 'archivadas o No Aplica coinciden';

  return (
    <Empty className="col-span-full border">
      <EmptyHeader>
        <EmptyMedia variant="icon">
          <SearchX aria-hidden="true" />
        </EmptyMedia>
        <EmptyTitle>
          <h3 className="contents">{archivadas ? 'Ninguna obra coincide' : 'Ninguna obra en curso coincide'}</h3>
        </EmptyTitle>
        <EmptyDescription>Probá con otro texto o quitá algún filtro.</EmptyDescription>
      </EmptyHeader>
      <EmptyContent>
        {hayArchCoincidencia && (
          <p className="m-0 flex flex-wrap items-center justify-center gap-1.5 text-[12.5px] text-muted-foreground">
            <Archive className="h-3 w-3 flex-none" aria-hidden="true" />
            <b className="font-semibold tabular-nums text-foreground">{archCoincN}</b>
            {archCoincTxt}
            <span className="text-border-strong" aria-hidden="true">
              ·
            </span>
            <Button type="button" variant="ghost" size="sm" onClick={onVerConArchivadas}>
              Ver con Archivadas
            </Button>
          </p>
        )}
        <div className="flex flex-wrap justify-center gap-2">
          {hayBusqueda && (
            <Button type="button" variant="outline" size="sm" onClick={onLimpiarBusqueda}>
              Limpiar búsqueda
            </Button>
          )}
          {hayControlRdtActivo && (
            <Button type="button" variant="outline" size="sm" onClick={onVerTodoElEquipo}>
              Ver todo el equipo
            </Button>
          )}
          {verLimpiarFiltros && (
            <Button type="button" variant="outline" size="sm" onClick={onLimpiarFiltros}>
              Limpiar filtros
            </Button>
          )}
        </div>
      </EmptyContent>
    </Empty>
  );
}

function ReferenciasFooter({ obras }: { obras: ObraCard[] }) {
  const clientesConColor = useMemo(() => {
    const vistos = new Map<string, ObraCard>();
    for (const o of obras) {
      if (o.color && o.cliente && !vistos.has(o.cliente)) vistos.set(o.cliente, o);
    }
    return [...vistos.values()].sort((a, b) => (a.cliente ?? '').localeCompare(b.cliente ?? ''));
  }, [obras]);

  return (
    <section aria-label="Referencias" className="mt-[34px] border-t border-border-subtle pt-[18px]">
      <h2 className="mb-3 text-[11px] font-[650] uppercase tracking-[0.08em] text-text-tertiary">Referencias</h2>
      <div className="flex flex-wrap gap-x-12 gap-y-7">
        <div>
          <h3 className="mb-2 text-[10.5px] font-[650] uppercase tracking-[0.07em] text-text-tertiary">
            Semáforo de vencimiento
          </h3>
          <div className="flex flex-col gap-[5px]">
            <span className="flex items-center gap-[7px] text-[12.5px] text-muted-foreground">
              <span className="h-[7px] w-[7px] flex-none rounded-full bg-destructive" />
              Vencido o vence en menos de 60 días
            </span>
            <span className="flex items-center gap-[7px] text-[12.5px] text-muted-foreground">
              <span className="h-[7px] w-[7px] flex-none rounded-full bg-warning opacity-85" />
              Vence en menos de 90 días
            </span>
            <span className="flex items-center gap-[7px] text-[12.5px] text-muted-foreground">
              <span className="h-[7px] w-[7px] flex-none rounded-full bg-success opacity-55" />
              Más de 90 días (atenuado: lo importante es el rojo)
            </span>
            <span className="text-[12.5px] text-text-tertiary">Sin punto = sin plazo cargado</span>
          </div>

          <h3 className="mb-2 mt-4 text-[10.5px] font-[650] uppercase tracking-[0.07em] text-text-tertiary">
            Control RDT
          </h3>
          <div className="flex flex-col gap-[5px]">
            {Object.entries(ENC_NOMBRES).map(([codigo, nombre]) => (
              <span key={codigo} className="flex items-center gap-[7px] text-[12.5px] text-muted-foreground">
                <span className="flex h-[18px] w-[18px] flex-none items-center justify-center rounded-full border border-border-strong bg-surface-2 text-[7.5px] font-[650] text-muted-foreground">
                  {encInitials(codigo)}
                </span>
                {nombre}
              </span>
            ))}
          </div>
        </div>

        <div className="min-w-[260px] flex-1">
          <h3 className="mb-2 text-[10.5px] font-[650] uppercase tracking-[0.07em] text-text-tertiary">
            Color por cliente{' '}
            <span className="font-medium normal-case tracking-normal">(de la lista Colores Clientes)</span>
          </h3>
          <div className="grid max-w-[620px] grid-cols-[repeat(auto-fill,minmax(180px,1fr))] gap-x-[18px] gap-y-1">
            {clientesConColor.map(o => {
              const acc = accentVariants(o.color);
              if (!acc) return null;
              return (
                <span
                  key={o.cliente}
                  className="flex items-center gap-[7px] text-[12.5px] text-muted-foreground"
                  style={{ '--dot-l': acc.l, '--dot-d': acc.d } as CSSProperties}
                >
                  <span className="h-2 w-2 flex-none rounded-full bg-[var(--dot-l)] dark:bg-[var(--dot-d)]" />
                  {o.cliente}
                </span>
              );
            })}
            <span className="flex items-center gap-[7px] text-[12.5px] text-muted-foreground">
              <span className="h-2 w-2 flex-none rounded-full bg-border-strong" />
              Sin color asignado
            </span>
          </div>
          <p className="mt-2.5 max-w-[68ch] text-[11.5px] text-text-tertiary">
            Los colores reales se atenúan un poco para usarse como acento (eran fondos de bloque en la
            app vieja): se bajan de saturación, los muy claros se oscurecen y los muy oscuros se aclaran
            en tema oscuro. Cliente sin color asignado → gris neutro.
          </p>
        </div>
      </div>
    </section>
  );
}

/** ¿El foco está en un campo de texto? (mismo patrón que estaEscribiendo de
 *  avivador-teclado.ts / pages/avivador.tsx: cada archivo con su propia copia,
 *  no hay helper centralizado — relevamiento §5). */
function estaEscribiendo(el: Element | null): boolean {
  if (!el) return false;
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || (el as HTMLElement).isContentEditable === true;
}

/** Anuncio sr-only del Enter del buscador (síntesis §2 «Buscador»: «Anunciar
 *  la categoría detectada»). */
function anuncioEnter(r: ResultadoEnterObras): string {
  switch (r.kind) {
    case 'vacio':
      return '';
    case 'token':
      return r.token.cat ? `Filtro agregado: ${r.token.cat}: ${r.token.label}` : `Búsqueda agregada: ${r.token.label}`;
    case 'controlRdt':
      return `Filtro agregado: Control RDT: ${ENC_NOMBRES[r.codigo] ?? r.codigo}`;
    case 'estadoObra':
      return `Filtro agregado: Estado de obra: ${r.valor}`;
    case 'estadoRedes':
      return `Filtro agregado: Estado de redes: ${r.valor}`;
  }
}

function ObrasContent({ obras }: { obras: ObraCard[] }) {
  // Filtros persistidos de esta pestaña (sessionStorage, no URL — decisión de
  // Juan del 21/9 que sigue valiendo; clave v4 nueva, contrato
  // FiltrosObras/ContratoObrasFiltros de obras-filtros-tipos.ts). Se lee UNA
  // vez al montar: el inicializador lazy de useState corre una sola vez.
  const [filtros, setFiltrosState] = useState<FiltrosObras>(() => leerFiltrosPersistidos() ?? limpiarFiltros(false));
  // Texto YA normalizado y con debounce de 250ms (MVP §5.1, sin cambios) —
  // estado de COMPONENTE, no del contrato (mismo criterio que el Avivador:
  // nunca filtrar contra `filtros.q` crudo).
  const [qNorm, setQNorm] = useState(() => normalizarBusqueda(filtros.q));
  const [popoverAbierto, setPopoverAbierto] = useState(false);
  const [anuncio, setAnuncio] = useState('');
  const buscadorRef = useRef<HTMLInputElement>(null);

  // Diálogo «Estado de redes» (design_estado_redes_obra.md §4): solo con
  // permiso de escritura (gestor/admin) — mismo gate que el resto de la app.
  const { data: me } = useMe();
  const puedeEscribir = me?.rol === 'gestor' || me?.rol === 'admin';
  const [estadoRedesCc, setEstadoRedesCc] = useState<string | null>(null);

  const archivadasId = useId();
  const archivadasDescId = useId();

  useEffect(() => {
    const timer = setTimeout(() => setQNorm(normalizarBusqueda(filtros.q)), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [filtros.q]);

  useEffect(() => {
    guardarFiltrosPersistidos(filtros);
  }, [filtros]);

  // «/» lleva el foco al buscador desde cualquier lado (components/filtros/atajos.ts).
  useEffect(() => {
    function onKeyDown(e: globalThis.KeyboardEvent): void {
      if (esAtajoBuscar(e, estaEscribiendo(document.activeElement))) {
        e.preventDefault();
        buscadorRef.current?.focus();
      }
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  const facetas = useMemo(() => contarFacetas(obras, filtros, qNorm), [obras, filtros, qNorm]);
  const filtered = useMemo(() => filtrar(obras, filtros, qNorm), [obras, filtros, qNorm]);
  const nActivos = useMemo(() => contarFiltros(filtros), [filtros]);
  const chips = useMemo(() => chipsActivos(filtros), [filtros]);
  const variante = varianteConteo(filtros.archivadas);

  // «N archivadas o No Aplica coinciden» del vacío (síntesis §2): solo se
  // calcula cuando hace falta (vacío + switch apagado). Separados (artboard
  // `Home.dc.html:527`, hallazgo #4 reui-y-artboards): `EmptyObras` arma el
  // texto de a 3 ramas (solo Archivado / solo No Aplica / mezcla), no puede
  // hacerlo con un total ya combinado.
  const archArchivadoCoincidentesN = useMemo(() => {
    if (filtros.archivadas || filtered.length > 0) return 0;
    return filtrar(obras, { ...filtros, archivadas: true }, qNorm).filter(o => o.estadoRdt === 'Archivado').length;
  }, [obras, filtros, qNorm, filtered.length]);
  const archNoAplicaCoincidentesN = useMemo(() => {
    if (filtros.archivadas || filtered.length > 0) return 0;
    return filtrar(obras, { ...filtros, archivadas: true }, qNorm).filter(o => o.estadoRdt === 'No Aplica').length;
  }, [obras, filtros, qNorm, filtered.length]);

  function setFiltros(patch: Partial<FiltrosObras>): void {
    setFiltrosState(f => ({ ...f, ...patch }));
  }

  function handleArchivadasChange(next: boolean): void {
    setFiltrosState(f => sanearFiltrosObras({ ...f, archivadas: next }));
  }

  function handleLimpiar(): void {
    setFiltrosState(f => limpiarFiltros(f.archivadas));
    setQNorm('');
    // Vuelve a los defaults Y borra lo persistido (decisión 1, BACKLOG 25/9):
    // «Limpiar» no apaga el switch, pero sí borra lo demás persistido.
    borrarFiltrosPersistidos();
  }

  function handleQuitarChip(key: ChipObraKey): void {
    setFiltrosState(f => quitarChip(f, key));
  }

  function handleQueryEnter(): void {
    if (!filtros.q.trim()) return;
    const resultado = detectarEnter(filtros.q, obras, filtros.archivadas);
    if (resultado.kind === 'vacio') return;
    setFiltrosState(f => aplicarEnter(f, resultado));
    setQNorm('');
    setAnuncio(anuncioEnter(resultado));
  }

  function onBuscadorKeyDown(e: KeyboardEvent<HTMLInputElement>): void {
    if (e.key === 'Enter') {
      // Con un IME componiendo, Enter confirma la composición: no es un filtro.
      if (e.nativeEvent.isComposing) return;
      e.preventDefault();
      handleQueryEnter();
      return;
    }
    if (e.key !== 'Escape') return;
    e.stopPropagation();
    // Esc limpia el texto SIN blur (síntesis §2 «Buscador»): en la home no
    // hay una grilla con flechas adonde volver, a diferencia del Avivador.
    const accion = accionEscBuscador(e.currentTarget.value);
    if (accion === 'limpiar') {
      e.preventDefault();
      setFiltros({ q: '' });
    }
  }

  return (
    <>
      <section aria-label="Filtros de obras" className="mb-2.5 flex flex-wrap items-center gap-2.5">
        <InputGroup className="min-w-[220px] flex-1 basis-[250px] bg-surface-card shadow-[0_1px_2px_rgba(20,20,22,0.04)] dark:bg-surface-card">
          <InputGroupAddon>
            <Search aria-hidden="true" strokeWidth={2} />
          </InputGroupAddon>
          <InputGroupInput
            ref={buscadorRef}
            type="text"
            value={filtros.q}
            onChange={e => setFiltros({ q: e.target.value })}
            onKeyDown={onBuscadorKeyDown}
            placeholder={PLACEHOLDER_BUSCADOR}
            aria-label={ARIA_BUSCADOR}
            aria-keyshortcuts={ATAJO_BUSCAR}
            autoComplete="off"
            spellCheck={false}
            enterKeyHint="search"
            className="text-ellipsis"
          />
        </InputGroup>

        <FiltrosObrasPopover
          obras={obras}
          filtros={filtros}
          facetas={facetas}
          abierto={popoverAbierto}
          onAbiertoChange={setPopoverAbierto}
          nActivos={nActivos}
          nBusqueda={filtros.tokens.length}
          puedeLimpiar={nActivos > 0}
          onLimpiar={handleLimpiar}
          onFiltrosChange={setFiltros}
          totalConEstosFiltros={filtered.length}
        />

        <Field orientation="horizontal" className="w-auto gap-2">
          <Switch
            id={archivadasId}
            checked={filtros.archivadas}
            onCheckedChange={handleArchivadasChange}
            aria-describedby={archivadasDescId}
          />
          <FieldLabel htmlFor={archivadasId} className="font-normal text-foreground">
            Archivadas
          </FieldLabel>
        </Field>
        <span id={archivadasDescId} className="sr-only">
          {puedeEscribir
            ? 'Suma las obras archivadas y No Aplica y habilita cambiar el estado de redes desde cada card.'
            : 'Suma las obras archivadas y No Aplica.'}
        </span>
      </section>

      <MetaObras
        n={filtered.length}
        total={obras.length}
        variante={variante}
        archivadas={filtros.archivadas}
        puedeEscribir={puedeEscribir}
        chips={chips}
        onQuitarChip={handleQuitarChip}
        onLimpiarTodo={handleLimpiar}
        focoSinChipsRef={buscadorRef}
      />
      <span role="status" className="sr-only">
        {anuncio}
      </span>

      <section aria-label="Listado de obras" className="grid grid-cols-[repeat(auto-fill,minmax(310px,1fr))] gap-3.5">
        {filtered.length === 0 ? (
          <EmptyObras
            hayBusqueda={filtros.q.trim().length > 0 || filtros.tokens.length > 0}
            hayControlRdtActivo={filtros.controlRdt !== TODO_EL_EQUIPO_OBRAS}
            hayOtrosFiltros={filtros.estadosRedes.length > 0 || filtros.estadosObra.length > 0}
            archivadas={filtros.archivadas}
            archArchivadoCoincidentesN={archArchivadoCoincidentesN}
            archNoAplicaCoincidentesN={archNoAplicaCoincidentesN}
            onLimpiarBusqueda={() => {
              setFiltros({ q: '', tokens: [] });
              setQNorm('');
            }}
            onVerTodoElEquipo={() => setFiltros({ controlRdt: TODO_EL_EQUIPO_OBRAS })}
            onVerConArchivadas={() => setFiltros({ archivadas: true })}
            onLimpiarFiltros={handleLimpiar}
          />
        ) : (
          filtered.map(o => (
            <ObraCardTile
              key={o.cc}
              obra={o}
              modoArchivo={filtros.archivadas}
              onEstadoRedes={puedeEscribir ? setEstadoRedesCc : undefined}
            />
          ))
        )}
      </section>

      <ReferenciasFooter obras={obras} />

      <EstadoRedesDialog cc={estadoRedesCc} onOpenChange={open => !open && setEstadoRedesCc(null)} />
    </>
  );
}

// Home real de la app (fase 7): grilla de obras con el patrón «Filtros» del
// Avivador v2 (R4a — plan_migracion_reui.md, sección «R4a — Home con
// Filtros»; artboards aprobados por Juan el 25/9, BACKLOG «Home de Obras con
// el patrón "Filtros"»). Skeleton SOLO durante la carga inicial del query —
// una vez cargado, todo el filtrado es instantáneo.
export function Obras() {
  const { data: obras, isPending, isError, refetch } = useObras();

  return (
    <div className="px-[26px] pb-12 pt-[22px]">
      <h1 className="mb-[18px] text-[19px] font-[650] tracking-[-0.015em] text-foreground">Obras</h1>
      {isPending ? (
        <SkeletonGrid />
      ) : isError ? (
        <ErrorState onRetry={() => void refetch()} />
      ) : (
        <ObrasContent obras={obras} />
      )}
    </div>
  );
}
